import * as React from "react";
import { graphql } from "gatsby";

import { StaticImage } from "gatsby-plugin-image";
import { motion } from "framer-motion";
import useVideoAutoplayOnIntersect from "../hooks/useVideoAutoplayOnIntersect";
import Layout from "../templates/layout";
import {
  Row,
  Video,
  Column,
  MediaWrapper,
  TextWrapper,
  SplashScreen,
} from "../components";
import { textFadeIn, decorationFadeIn } from "../animations";

import videoBilderstoeckchen from "../videos/ASW-GB-Bilderstoeckchen.mp4";
import videoGeschaeftsfuehrung from "../videos/ASW-GB-Geschaeftsfuehrung.mp4";
import ImgHouse from "../images/notwendigkeiten-house.inline.svg";
import generationsClip1 from "../videos/generationen-clip-1.mp4";
import generationsClip2 from "../videos/generationen-clip-2.mp4";
import introClip from "../videos/intro-clip.mp4";

const IndexPage = ({ data }) => {
  const [introContainerRef, introClipRef] = useVideoAutoplayOnIntersect({
    threshold: 0.1,
  });

  const [generationContainer1Ref, generationClip1Ref] =
    useVideoAutoplayOnIntersect({
      threshold: 0.1,
    });

  const [generationContainer2Ref, generationClip2Ref] =
    useVideoAutoplayOnIntersect({
      threshold: 0.1,
    });

  return (
    <Layout
      pageTitle={data.site.siteMetadata.title}
      pageDescription={data.site.siteMetadata.description}
      absolutePath={data.site.siteMetadata.siteURL}
    >
      <SplashScreen />

      <section ref={introContainerRef}>
        <Row style={{ minHeight: "50vh" }}>
          <Column
            style={{
              alignSelf: "flex-start",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <motion.div
              variants={decorationFadeIn}
              viewport={decorationFadeIn.viewportProps}
              initial="initial"
              whileInView="whileInView"
            >
              <video
                ref={introClipRef}
                className="intro-clip-1"
                muted
                playsInline
              >
                <source src={introClip} type="video/mp4" />
              </video>
            </motion.div>
            <TextWrapper maxWidth="760px">
              <motion.div
                variants={textFadeIn}
                viewport={textFadeIn.viewportProps}
                initial="initial"
                whileInView="whileInView"
              >
                <p className="intro-text">
                  Was sollten wir erzählen über das vergangene Jahr?
                  <br />
                  Die Herausforderungen in der Welt sind vielfältig und werden
                  vom Gartenzaun bis ins Parlament kontrovers diskutiert.
                  Gefühlt gibt es überall Krisen. Gerade deshalb machen wir
                  weiter. Mit bezahlbarem Wohnraum und Werten, die nicht nur
                  Zahlen sind.
                </p>
              </motion.div>
            </TextWrapper>
          </Column>
        </Row>
      </section>

      <section id="clouth">
        <Row style={{ paddingBottom: "var(--space-m)" }}>
          <Column>
            <TextWrapper maxWidth="560px">
              <motion.div
                variants={textFadeIn}
                viewport={textFadeIn.viewportProps}
                initial="initial"
                whileInView="whileInView"
              >
                <motion.h3 variants={textFadeIn}>
                  Clouth wird richtig gut
                </motion.h3>
                <motion.p variants={textFadeIn}>
                  Wo früher weltweit erfolgreiche Gummiprodukte produziert
                  worden sind, entsteht seit 2014 ein neues, vielfältiges
                  Wohngebiet: das Clouth-Quartier in Nippes. Hier hat die
                  Aachener 2020 eines der letzten heißbegehrten Grundstücke
                  erworben. Inklusive klarer Auflagen der Kölner
                  Stadtentwicklungsgesellschaft „moderne stadt“, denn das 14,5
                  ha große Areal ist als neues Quartier mit besonderen
                  Anforderungen definiert.
                </motion.p>
              </motion.div>
            </TextWrapper>
          </Column>
          <Column>
            <MediaWrapper style={{ alignSelf: "flex-end" }} pullRight>
              <StaticImage
                className="clouth-img-1"
                src="../images/clouth-img-1.jpg"
                alt="Baugerüst am Clouth-Quartier in  Nippes"
                loading="lazy"
                layout="constrained"
                width={660}
                objectFit={"contain"}
                style={{
                  alignSelf: "flex-end",
                }}
              />

              <motion.div
                variants={decorationFadeIn}
                viewport={{
                  ...decorationFadeIn.viewportProps,
                  margin: "0px 0px 340px 0px",
                }}
                initial="initial"
                whileInView="whileInView"
              >
                <StaticImage
                  className="clouth-decoration-1"
                  src="../images/clouth-deco-1.png"
                  alt=""
                  width={233}
                  height={715}
                  loading="lazy"
                  layout="constrained"
                  transformOptions={{
                    fit: "inside",
                  }}
                />
              </motion.div>
            </MediaWrapper>
          </Column>
        </Row>

        <Row
          className="hidden-mobile"
          style={{ paddingBottom: "var(--space-4xl)" }}
        >
          <Column>
            <motion.div
              variants={decorationFadeIn}
              viewport={decorationFadeIn.viewportProps}
              initial="initial"
              whileInView="whileInView"
            >
              <StaticImage
                className="clouth-decoration-2"
                src="../images/clouth-deco-2.png"
                alt=""
                width={613}
                height={697}
                loading="lazy"
                layout="constrained"
                transformOptions={{
                  fit: "inside",
                }}
              />
            </motion.div>
            <MediaWrapper style={{ alignSelf: "center" }} offset={80}>
              <StaticImage
                className="clouth-img-2"
                src="../images/clouth-img-2.jpg"
                alt="Arbeiten am Clouth-Quartier in Nippes"
                loading="lazy"
                layout="constrained"
                width={490}
              />
            </MediaWrapper>
          </Column>
        </Row>
      </section>

      <section id="spaziergang" style={{ position: "relative", zIndex: 1 }}>
        <Row className="reverse-mobile">
          <Column>
            <Video
              mediaWrapperProps={{ pullLeft: true }}
              video={videoGeschaeftsfuehrung}
              poster={
                <StaticImage
                  src="../images/spaziergang-img.jpg"
                  alt=""
                  loading="lazy"
                  layout="constrained"
                  objectFit={"contain"}
                />
              }
            />
          </Column>
          <Column>
            <TextWrapper
              className="text-spaziergang"
              maxWidth="calc(490px + var(--container--space))"
            >
              <motion.div
                variants={textFadeIn}
                viewport={textFadeIn.viewportProps}
                initial="initial"
                whileInView="whileInView"
              >
                <motion.h4 variants={textFadeIn}>(K)ein Spaziergang</motion.h4>
                <motion.p variants={textFadeIn}>
                  Am Kirchenportal eine Hochzeitsgesellschaft, auf dem Platz
                  davor Marktstände. Mütter schieben Kinderwagen über das
                  Kopfsteinpflaster, von Fahrradlenkern baumeln Einkaufstaschen.
                  Im Schatten der Severinskirche ist einiges los an diesem
                  Vormittag. Genau wie im Rest der Domstadt und der Welt.
                  {/*<br />
                  <br />
                  Wir sind mit der Geschäftsführung unterwegs – in Köln und
                  durch herausfordernde Zeiten.*/}
                </motion.p>
              </motion.div>
            </TextWrapper>
          </Column>
        </Row>
      </section>

      <motion.section
        id="generationen"
        variants={textFadeIn}
        viewport={textFadeIn.viewportProps}
        initial="initial"
        whileInView="whileInView"
      >
        <div ref={generationContainer1Ref}>
          <Row containerSpace={"large"} style={{ paddingBottom: 0 }}>
            <Column>
              <video
                ref={generationClip1Ref}
                className="generations-clip-1"
                muted
                playsInline
              >
                <source src={generationsClip1} type="video/mp4" />
              </video>

              <TextWrapper maxWidth="760px">
                <motion.h3
                  variants={textFadeIn}
                  aria-label="Ein Projekt für Generationen"
                >
                  Ein Projekt
                  <br />
                  für
                  <br />
                  Genera&shy;tionen
                </motion.h3>
              </TextWrapper>
            </Column>
          </Row>
          <Row
            containerSpace={"large"}
            style={{ paddingTop: "var(--space-m)" }}
          >
            <Column>
              <TextWrapper maxWidth="490px">
                <motion.p variants={textFadeIn}>
                  Liegen hier noch Grünflächen zwischen Häusern? Oder sind es
                  nicht vielmehr die Häuser, die es sich zwischen Wiesen, Blumen
                  und breiten Wegen gemütlich gemacht haben? So genau weiß man
                  es nicht in Köln-Bilderstöckchen, wo die Aachener 18
                  Wohnblöcke mit insgesamt 410 Wohnungen bewirtschaftet. Mit
                  gewöhnlichen Stadtquartieren ist die Siedlung hier jedenfalls
                  kaum zu vergleichen – sie ist besonders. Entstanden ist sie in
                  ihrer heutigen Größe in den 1960er-Jahren, nach einem Konzept,
                  das Generationen zusammenbringen wollte und damals
                  Vorbildcharakter hatte.
                </motion.p>
              </TextWrapper>
            </Column>
            <Column>
              <Video
                mediaWrapperProps={{
                  pullRight: true,
                  className: "generationen-media",
                }}
                video={videoBilderstoeckchen}
                poster={
                  <StaticImage
                    src="../images/generationen-img-1.jpg"
                    alt="Häuser aus der Vogelperspektive"
                    loading="lazy"
                    layout="constrained"
                    objectFit={"contain"}
                  />
                }
              />
            </Column>
          </Row>
        </div>

        <div ref={generationContainer2Ref}>
          <Row style={{ paddingTop: 0, paddingBottom: "var(--space-4xl)" }}>
            <Column>
              <video
                ref={generationClip2Ref}
                className="generations-clip-2"
                muted
                playsInline
              >
                <source src={generationsClip2} type="video/mp4" />
              </video>
              <MediaWrapper pullLeft offset={80}>
                <StaticImage
                  src="../images/generationen-img-2.jpg"
                  alt="Mitarbeiter der Aachener auf einer Baustelle"
                  loading="lazy"
                  width={644}
                  layout="constrained"
                />
              </MediaWrapper>
            </Column>
          </Row>
        </div>
      </motion.section>

      <section id="notwendigkeiten">
        <motion.div
          variants={textFadeIn}
          viewport={textFadeIn.viewportProps}
          initial="initial"
          whileInView="whileInView"
        >
          <Row containerSpace={"large"} className="notwendigkeiten-row">
            <Column>
              <motion.div
                variants={decorationFadeIn}
                viewport={decorationFadeIn.viewportProps}
                initial="initial"
                whileInView="whileInView"
              >
                <StaticImage
                  className="necessity-decoration-1"
                  src="../images/notwendigkeiten-deco-1.png"
                  alt=""
                  loading="lazy"
                  layout="constrained"
                />
              </motion.div>
              <TextWrapper maxWidth="570px">
                <motion.h3 variants={textFadeIn}>
                  Notwen&shy;digkeiten sehen
                </motion.h3>
                <motion.p variants={textFadeIn}>
                  Der Begriff „Nachhaltigkeit“ tritt unter anderem aufgrund von
                  Klimawandel, Ressourcenverbrauch und Artensterben in den
                  letzten Jahren immer mehr in den Vordergrund. Aber was steckt
                  noch hinter dem Buzzword „Nachhaltigkeit“? Und was genau
                  bedeutet es für die Aachener? Um das heraus zu finden, haben
                  wir als ersten strategischen Schritt die wesentlichen
                  Themenfelder für unser Unternehmen definiert und zur
                  nachhaltigen Basis für unser zukünftiges Handeln gemacht.
                </motion.p>
              </TextWrapper>
            </Column>
            <Column style={{ alignSelf: "flex-end" }}>
              <MediaWrapper pullRight>
                <StaticImage
                  className="necessity-img-1"
                  src="../images/notwendigkeiten-img-1.jpg"
                  alt=""
                  loading="lazy"
                  width={720}
                  layout="constrained"
                  objectFit={"cover"}
                />
              </MediaWrapper>
            </Column>
          </Row>
        </motion.div>

        <Row className="reverse-mobile">
          <Column className="hidden-mobile">
            <MediaWrapper style={{ width: "100%" }}>
              <motion.figure
                variants={textFadeIn}
                viewport={textFadeIn.viewportProps}
                initial="initial"
                whileInView="whileInView"
              >
                <ImgHouse />
                <motion.figcaption variants={textFadeIn}>
                  Wir verstehen unsere wesentlichen Themenfelder als zentrales
                  Element unserer langfristigen, nachhaltigen Ausrichtung. Sie
                  bilden die schützende Dämmung unseres Strategiehauses, die
                  abschließend von einem beständigen Mauerwerk ummantelt wird,
                  dem der Kerngedanke der Schöpfungsbewahrung innewohnt.
                </motion.figcaption>
              </motion.figure>
            </MediaWrapper>
          </Column>
          <Column>
            <TextWrapper maxWidth="460px" style={{ alignSelf: "center" }}>
              <motion.div
                as={motion.div}
                variants={textFadeIn}
                viewport={textFadeIn.viewportProps}
                initial="initial"
                whileInView="whileInView"
              >
                <motion.ul variants={textFadeIn}>
                  <li>Klimawandel</li>
                  <li>Energetische Modernisierung</li>
                  <li>Mitarbeiterzufriedenheit</li>
                  <li>Kreislaufwirtschaft</li>
                  <li>Digitalisierung</li>
                  <li>Compliance</li>
                  <li>Nachhaltiges und langfristiges Wachstum</li>
                  <li>Kundenzufriedenheit</li>
                  <li>Standortentwicklung</li>
                </motion.ul>
              </motion.div>
            </TextWrapper>
          </Column>
        </Row>
      </section>
    </Layout>
  );
};

export default IndexPage;

export const query = graphql`
  query PageQuery {
    site {
      siteMetadata {
        title
        description
        siteURL
      }
    }
  }
`;
